// La pestaña Ocio. Es la rara de las cinco y conviene saber por qué antes de tocarla.
//
// No es un buscador: es un solo campo, sin fechas, sin pasajeros y sin botón. Y no es un
// autocompletado por AJAX como los otros: se descarga **el catálogo entero de una vez** y se
// filtra en el cliente. Elegir una fila NO rellena un formulario — navega directamente.
//
// La lista que se ofrece son dos cosas mezcladas, y en este orden:
//   1. las CIUDADES, sacadas de los `destinos` de cada producto y deduplicadas por
//      nombre+id; llevan chincheta y van a `Search/go/Destino/{id}`;
//   2. los PRODUCTOS cuyo `mostrarOcio` no sea false; llevan billete y van a `Producto/{id}`.
//
// Y una diferencia con los campos de estación que sí importa: aquí no se ofrece nada hasta
// que el cliente escribe la primera letra (en la home, `if (!val) return false`).
import { autocompletar, normalizar } from './autocompletado.js'
import { abrirPanelMovil, cerrarPanelMovil } from './panel-movil.js'

// Los dos iconos que la home sirve desde /front/svg/.
const ICONO_PRODUCTO = 'front/svg/svg-ticket-renfe.svg'
const ICONO_CIUDAD = 'front/svg/chincheta-renfe.svg'

/**
 * @param {import('./buscador.js').Buscador} buscador
 * @param {string} bloque
 */
export function crearOcio (buscador, bloque) {
  // Un solo selector, sin comas: `nodo()` antepone el bloque al selector que se le pasa, y
  // con una lista el prefijo se pegaría sólo a la primera alternativa. Las otras quedarían
  // sueltas y `querySelector` devolvería el primer acierto EN ORDEN DE DOCUMENTO, que aquí
  // es el campo de estación de otra pestaña.
  const campo = buscador.nodo(bloque, '[data-rvb="buscadorActividades"]')
  if (!campo) return null

  const cargando = buscador.nodo(bloque, '.rvb-loader')
  const contenedor = buscador.nodo(bloque, '.rvb-contenedor-buscador')
  let catalogo = null      // null = todavía no se ha pedido
  let pidiendo = null

  /**
   * Arma la lista una sola vez, la pida quien la pida: el arranque del buscador, el clic en la
   * pestaña o la primera letra que se escriba en el campo. Se pide al cargar la página, como en
   * la home — son cientos de filas, pero esperar a que alguien entre en Ocio dejaba la pestaña
   * abriéndose con el spinner puesto, y en el buscador de siempre el campo ya está listo.
   */
  async function catalogoListo (señal) {
    if (catalogo) return catalogo
    if (pidiendo) return pidiendo
    pidiendo = (async () => {
      const filas = await buscador.datos.catalogoOcio(señal)
      const productos = Array.isArray(filas) ? filas : []

      // Las ciudades salen de los destinos de los productos, deduplicadas por nombre+id.
      const vistas = new Set()
      const ciudades = []
      for (const producto of productos) {
        if (!producto || !Array.isArray(producto.destinos)) continue
        for (const ciudad of producto.destinos) {
          if (!ciudad || ciudad.nombre === undefined) continue
          const clave = ciudad.nombre + '|' + ciudad.id
          if (vistas.has(clave)) continue
          vistas.add(clave)
          ciudades.push({ esOcio: false, nombre: ciudad.nombre, id: ciudad.id, url: false })
        }
      }

      // Los productos que se pueden enseñar, marcados como ocio.
      const visibles = productos
        .filter((p) => p && p.mostrarOcio !== false)
        .map((p) => Object.assign({}, p, { esOcio: true }))

      // Ciudades primero, productos después: es el orden de la home.
      catalogo = ciudades.concat(visibles)
      if (cargando) cargando.classList.add('rvb-oculto')
      if (contenedor) contenedor.classList.remove('rvb-oculto')
      return catalogo
    })()
    return pidiendo
  }

  // El campo de Ocio se comporta en móvil como los de estación: la home le pone los mismos
  // manejadores (es un `.station2Input`), con el apaño de iOS incluido.
  campo.addEventListener('focus', () => abrirPanelMovil(campo))
  campo.addEventListener('blur', () => cerrarPanelMovil(campo))

  const ac = autocompletar({
    campo,
    overlays: buscador.overlays,
    // La lista de Ocio no se parece a la de las estaciones: lleva miniatura, ocupa el ancho
    // del recuadro entero y las filas van separadas por una línea. En la home la pinta un
    // desplegable propio, `.autocomplete-items`; aquí es el mismo autocompletado de siempre
    // con esa clase puesta, y el CSS generado le da la forma.
    clases: 'rvb-autocomplete-items',
    anclaje: campo.closest('.rvb-contenedor-buscador') || campo,
    // Aquí no se ofrece nada con el campo vacío, a diferencia de las estaciones.
    minimo: 1,
    buscar: async (q, señal) => {
      const lista = await catalogoListo(señal)
      const busca = normalizar(q)
      if (!busca) return []
      // Igual que la home: coincidencia en cualquier parte del nombre, sin acentos.
      return lista.filter((item) => normalizar(item.nombre).indexOf(busca) !== -1)
    },
    pintar: (item, resaltar, escrito) => {
      const icono = buscador.datos.origen + '/' + (item.esOcio ? ICONO_PRODUCTO : ICONO_CIUDAD)
      // Sin negrita en lo escrito: la home tiene ese trozo COMENTADO en su desplegable de
      // Ocio --pinta el nombre entero y ya-- a diferencia del de estaciones, que sí resalta.
      const nombre = String(item.nombre == null ? '' : item.nombre)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      return '<img src="' + icono + '" alt="" class="rvb-miniBuscador">'
        + '<span class="rvb-name">' + nombre + '</span>'
    },
    alElegir: (item) => navegar(item),
  })

  /** Elegir una fila no rellena nada: se va. */
  function navegar (item) {
    buscador.cargando()

    if (item.esOcio) {
      // La analítica sólo se dispara para los productos, no para las ciudades. Es así hoy.
      buscador.analiticaAdobe('clicBuscarBillete', { actividad: item.nombre, tipoServicio: 'Ocio' })
      buscador.irA(buscador.datos.url('Producto/' + item.id))
    } else {
      buscador.irA(buscador.datos.url('Search/go/Destino/' + item.id))
    }
  }

  return {
    /** Para las pruebas: fuerza la descarga sin escribir en el campo. */
    catalogoListo,
    destruir () { ac.destruir() },
  }
}
